import { ReactNode } from 'react';
import Image from 'next/image';
import { Logo } from '@restworld/ui-ds-base';
import { cn, customImageLoader, getAssetSrc } from '@restworld/utils-common';

export type LoginLayoutProps = {
  children: ReactNode;
  /** Rendered under the form: at the bottom of the page on mobile, right below the form from tablet up. */
  footer?: ReactNode;
  /** Below 768px the photo is dropped, so the form keeps the whole screen (e.g. the code step, with the keyboard open). */
  hideImageOnMobile?: boolean;
  imageSrc?: string;
};

// Login page layout.
// - Mobile (<768) and tablet (768–1023): one column. Stacked logo, form, then the photo filling what is left.
// - Desktop (≥1024): two equal columns. Horizontal logo top-left with the form centred under it, photo on the right.
export const LoginLayout = ({
  children,
  footer,
  hideImageOnMobile = false,
  imageSrc = getAssetSrc('/utils-assets/cameriere-carrello-corridoio-hotel.webp'),
}: LoginLayoutProps) => (
  <div className="flex w-full min-h-[100dvh] bg-neutral-10 lg:h-[100dvh]">
    <div className="flex flex-1 flex-col gap-10 px-4 pt-5 pb-4 md:p-10 lg:flex-row">
      <div
        className={cn(
          'relative flex flex-col items-center gap-10 w-full lg:flex-1 lg:min-h-0 lg:overflow-y-auto',
          hideImageOnMobile && 'flex-1 md:flex-none'
        )}
      >
        <Logo variant="stacked" title="Restworld" rColor="#fafafa" className="h-12 w-auto text-purple-700 lg:hidden" />
        <Logo
          variant="horizontal"
          title="Restworld"
          rColor="#fafafa"
          className="hidden lg:block lg:absolute lg:top-0 lg:left-0 h-8 w-auto text-purple-700"
        />
        <div className={cn('flex flex-col w-full max-w-80 lg:my-auto', hideImageOnMobile && 'flex-1 md:flex-none')}>
          {children}
          {footer ? <div className="flex justify-center mt-auto pt-6 md:mt-0">{footer}</div> : null}
        </div>
      </div>
      <div
        className={cn(
          'relative w-full flex-1 min-h-60 overflow-hidden rounded-2xl lg:min-h-0',
          hideImageOnMobile && 'hidden md:block'
        )}
      >
        <Image
          src={imageSrc}
          alt=""
          fill
          priority
          sizes="(min-width: 1024px) 50vw, 100vw"
          loader={customImageLoader}
          className="object-cover"
        />
      </div>
    </div>
  </div>
);
